import React from 'react';
import useLeaderboard from '../hooks/useLeaderboard';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../components/ui/table';
import { Trophy, RefreshCw } from 'lucide-react';

// Top-3 row tints (gold / silver / bronze).
const RANK_ROW_CLASS = {
  1: 'bg-amber-50 dark:bg-amber-950/30',
  2: 'bg-slate-50 dark:bg-slate-900/40',
  3: 'bg-orange-50 dark:bg-orange-950/30',
};
const RANK_BADGE_CLASS = {
  1: 'bg-amber-200 text-amber-900 border-amber-300',
  2: 'bg-slate-200 text-slate-900 border-slate-300',
  3: 'bg-orange-200 text-orange-900 border-orange-300',
};

function relTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

export default function Leaderboard() {
  const { rows, loading, error, refresh } = useLeaderboard();

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Trophy className="h-6 w-6 text-amber-500" />
            Leaderboard
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Bug-hunt scoreboard. Points are awarded when an admin accepts your
            feedback: <strong>blocker = 5</strong>, <strong>major = 3</strong>,
            <strong> minor = 1</strong>. Same name across devices is the same
            person.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refresh}
          disabled={loading}
          title="Refresh"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {loading && rows.length === 0 && (
        <div className="text-sm text-muted-foreground">Loading…</div>
      )}
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="rounded-md border bg-muted/30 px-4 py-12 text-center">
          <Trophy className="h-12 w-12 text-muted-foreground/40 mx-auto mb-3" />
          <div className="text-sm text-muted-foreground">
            No one has earned points yet. Be the first — find a bad translation
            on the storefront and submit it via the feedback widget.
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16 text-center">Rank</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="w-28 text-right">Accepted</TableHead>
              <TableHead className="w-28 text-right">Points</TableHead>
              <TableHead className="w-40">Last submission</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, idx) => {
              const rank = idx + 1;
              return (
                <TableRow key={`${r.name}-${idx}`} className={RANK_ROW_CLASS[rank] || ''}>
                  <TableCell className="text-center">
                    {rank <= 3 ? (
                      <Badge variant="outline" className={RANK_BADGE_CLASS[rank]}>
                        {rank}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">{rank}</span>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{r.name || 'Anonymous'}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.accepted_count}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">
                    {r.points}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {relTime(r.last_submission)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
